'use client';

import { useTranslation } from '@root/src/i18n/client';
import MobileCollectionCarousel from './MobileCollectionCarousel';
import CollectionCard from '../../collection/collectionCard';
import {
  SharedSectionBackground,
  sharedSectionRootStyle,
} from '../sharedSectionBackground';

interface CollectionItem {
  slug: string;
  name?: string;
  title?: string;
  description?: string;
  image?: string;
  icon?: string;
}

interface AllCollectionProps {
  collections?: CollectionItem[];
}

export default function AllCollection({ collections = [] }: AllCollectionProps) {
  const { t } = useTranslation('common');
  const subtitle = t('lytra.collectionSectionBadge');
  const mainTitle = t('lytra.collectionSectionTitle');

  const allCollectionsData: CollectionItem[] = Array.isArray(collections)
    ? collections
    : [];

  const fallbackItems = Array.from({ length: 5 }).map((_, index) => ({
    slug: `collection-${index + 1}`,
  }));



  const sampleMeta = [
    {
      name: 'Trà xanh',
      description: 'Thanh mát, tốt cho sức khỏe',
      image: '/images/tea-green.jpg',
      icon: '/icons/leaf.svg',
    },
    {
      name: 'Trà ô long',
      description: 'Hương thơm đặc trưng, vị ngọt hậu',
      image: '/images/tea-oolong.jpg',
      icon: '/icons/teapot.svg',
    },
    {
      name: 'Trà đen',
      description: 'Đậm đà, ấm nóng đầy năng lượng',
      image: '/images/tea-black.jpg',
      icon: '/icons/leaf.svg',
    },
    {
      name: 'Trà thảo mộc',
      description: 'Tự nhiên, an lành cho tâm và thân',
      image: '/images/tea-herbal.jpg',
      icon: '/icons/flower.svg',
    },
    {
      name: 'Trà phổ nhĩ',
      description: 'Hương vị cổ truyền tốt cho tiêu hóa',
      image: '/images/tea-pu-erh.jpg',
      icon: '/icons/cup.svg',
    },
  ];

  const dataToRender = (allCollectionsData?.length ? allCollectionsData : fallbackItems).slice(0, 5);

  const renderCard = (item, index) => {
    const meta =
      item?.name || item?.title
        ? {
          name: item.name || item.title,
          description: item.description,
          image: item.image,
          icon: item.icon,
        }
        : sampleMeta[index];

    return (
      <CollectionCard
        key={item.slug || index}
        slug={item.slug}
        name={meta.name}
        description={meta.description}
        image={meta.image}
      />
    );
  };

  return (
    <div
      className="relative overflow-hidden py-10 sm:py-14 lg:py-14"
      style={sharedSectionRootStyle}
    >
      <SharedSectionBackground
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 1280px"
      />

      {/* Decorative Top Elements */}
      <div className="absolute left-0 right-0 top-0 flex justify-center">
        <div className="h-px w-32 bg-gradient-to-r from-transparent via-[#c6a869] to-transparent" />
      </div>

      {/* Header Section */}
      <div className="relative z-10 mx-auto mb-10 max-w-[1480px] px-3 text-center sm:mb-12 sm:px-6 lg:mb-10 lg:px-8">
        <div className="mb-3 inline-block rounded-full bg-[#c6a869]/10 px-4 py-1.5">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#c6a869]">
            {subtitle}
          </span>
        </div>

        <h2
          className="mt-4 text-3xl leading-tight text-[#2f3c30] sm:text-4xl lg:text-5xl"
          style={{ fontFamily: 'Georgia, Times New Roman, serif', fontWeight: 400 }}
        >
          {mainTitle}
        </h2>

        <div className="mt-4 flex items-center justify-center gap-3">
          <span className="h-px w-12 bg-gradient-to-r from-transparent to-[#d8c6a1]" />
          <span className="text-lg text-[#d2b57a]">✦</span>
          <span className="h-px w-12 bg-gradient-to-l from-transparent to-[#d8c6a1]" />
        </div>

        <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-[#6e6758] sm:text-base">
          {t('lytra.collectionSectionDesc')}
        </p>
      </div>

      {/* Collection Grid */}
      <div className="relative z-10 mx-auto max-w-[1480px] px-3 sm:px-6 lg:px-8">
        <div className="hidden sm:block">
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-5 lg:gap-8">
            {dataToRender?.map((item, index) => renderCard(item, index))}
          </div>
        </div>

        <MobileCollectionCarousel>
          {dataToRender?.map((item, index) => renderCard(item, index))}
        </MobileCollectionCarousel>
      </div>

      {/* Decorative Bottom Elements */}
      <div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2">
        <div className="flex gap-2">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-1 w-1 rounded-full bg-[#c6a869]/40"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
